import { storeToRefs } from 'pinia';
import { computed, ref } from '@nuxtjs/composition-api';
import { PRODUCT_TYPE } from '@/constants/ProductType';
import { CUSTOMER_SUBSCRIPTION_TYPE } from '~/constants/CheckoutFlow';
import { PURCHASE_CATEGORY } from '~/constants/PurchaseCategory';
import { VALIDATION_STATUS } from '~/constants/ValidationStatus';
import { BUNDLE_TYPE } from '@/constants/BundleType';
import { useCartStore } from '~/stores/useCartStore';
import useCartScenarios from '~/composable/useCartScenarios';
import useProductType from '~/composable/useProductType';
import usePersistedState from '~/composable/payment/usePersistedState';
import usePlaymix from '~/composable/usePlaymix';
import useErrorHandler from '~/composable/useErrorHandler';

export default function useTlmCart (appContext, cart = ref({})) {
  const hubbleClient = appContext.$hubble;


  if (!appContext || !hubbleClient) {
    throw new Error('You must provide the nuxt app context/hubbleClient to use this composable');
  }
  const cartStore = useCartStore()
  const {
    cprNumber,
    cvrNumber,
    isProtectedAddress,
    paymentRef,
    actionResponse
  } = storeToRefs(cartStore)
  const { removePersistedState } = usePersistedState(appContext)
  const { setScenario, getAttrFromCartScenario, getScenario, deleteCartScenarioCookie } = useCartScenarios(appContext)
  const { isMobileBroadBandSubscription, isSubscription, isMobileVoiceSubscription } = useProductType()
  const { validatePlayMixServicesInCart } = usePlaymix('playmix-4', hubbleClient)
  const { redirectToErrorPage } = useErrorHandler()
  if (!cart.value || Object.keys(cart.value).length < 1) {
    cart.value = cartStore.getCart
  }
  const loading = ref(false)
  const notificationAvail = ref(false)
  const notificationMessage = ref('')
  const createCart = async () => {
    notificationAvail.value = false;
    loading.value = true;
    const res = await hubbleClient.cart.createCart();
    actionResponse.value = res.data;
    cart.value = actionResponse.value.responseObject;
    cartStore.setCart(cart.value)
    appContext.$storage.setLocalStorage('tlmCartID', cart.value.id);
    loading.value = false;
    notificationAvail.value = true;
    notificationMessage.value = actionResponse.value.actionMessage;
    return res;
  }
  const addCartItem = async ({ sku, channelKey = '', installmentPeriod = 0, mixProducts = null, categoryId = null, channelSplit = null }) => {
    if (!isCartEmpty.value) {
      await emptyCart()
    }
    let playMixServicesInCartValid = true
    if (mixProducts !== null) {
      playMixServicesInCartValid = await validatePlayMixServicesInCart(mixProducts, channelKey);
    }
    const item = {
      cartItems: [{
        sku,
        channelKey,
        installmentPeriod,
        mixProducts
      }],
      categoryId
    }
    if (playMixServicesInCartValid) {
      loading.value = true;
      const res = await hubbleClient.cart.addCartItem(cart.value.id, item, channelSplit)
        .catch(err => {
          console.error('error', err)
          redirectToErrorPage('play-mix-mismatched')
        });
      loading.value = false;
      actionResponse.value = res.data;
      if (actionResponse.value.actionStatus === VALIDATION_STATUS.ERROR) {
        redirectToErrorPage('play-mix-mismatched')
      } else {
        cart.value = actionResponse.value.responseObject;
        notificationAvail.value = true;
        notificationMessage.value = actionResponse.value.actionMessage;
        setScenario(cart.value, 'categoryId', categoryId)
        removePersistedState()
        return cart.value;
      }
    } else {
      redirectToErrorPage('play-mix-mismatched')
    }
    return false;
  }
  const deleteCart = () => {
    /**
     * commenting it out as we dont intend to delete the cart for now
     * but it might be needed in future
     */
    // loading.value = true;
    // const res = await hubbleClient.cart.deleteCart(cart.value.id);
    // loading.value = false;
    cart.value = {};
    deleteCartCookie()
    deleteCartScenarioCookie()
    cartStore.setCart(cart.value)
    // actionResponse.value = res.data;
    // notificationAvail.value = true;
    // notificationMessage.value = actionResponse.value.actionMessage;
    // return res;
  }
  const deleteCartCookie = () => {
    appContext.$storage.removeLocalStorage('tlmCartID');
  }


  const checkIfCartExist = () => {
    let id;
    if (cart?.value && Object.keys(cart.value).length > 0) {
      id = cart.value.id;
    } else if (id === undefined && process.client) {
      id = appContext.$storage.getLocalStorage('tlmCartID');
    }
    return id;
  }
  const getCartById = async (cartID) => {
    loading.value = true;
    const res = await hubbleClient.cart.getCart(cartID);
    loading.value = false;
    return res;
  }
  const emptyCart = async () => {
    loading.value = true;
    const cartId = checkIfCartExist();
    const res = await hubbleClient.cart.emptyCart(cartId);
    loading.value = false;
    actionResponse.value = res.data;
    if (actionResponse.value.actionStatus === VALIDATION_STATUS.ERROR) {
      cart.value = null
      deleteCartCookie()
      await loadOrCreateCart()
    } else {
      cart.value = actionResponse.value.responseObject;
      deleteCartScenarioCookie()
      notificationAvail.value = true;
      notificationMessage.value = actionResponse.value.actionMessage;
      return res;
    }
  }
  const deleteCartGroupItem = async (sku) => {
    const cartItemGroupUpdate = {
      cartID: cart.value.id,
      sku
    }
    loading.value = true;
    const res = await hubbleClient.cart.deleteCartGroupItem(cartItemGroupUpdate);
    loading.value = false;
    actionResponse.value = res.data;
    cart.value = actionResponse.value.responseObject;
    notificationAvail.value = true;
    notificationMessage.value = actionResponse.value.actionMessage;
    return res;
  }

  const createCartOrder = async (cartID = cart?.value?.id, paymentReference = paymentRef.value) => {
    if (!cartID) {
      throw new Error('Cant create order without cartID');
    }

    loading.value = true;
    const res = await hubbleClient.cart.createCartOrder(
      cartID,
      paymentReference
    );
    loading.value = false;
    cart.value = actionResponse.value.responseObject;
    actionResponse.value = res.data;
    notificationAvail.value = true;
    notificationMessage.value = actionResponse.value.actionMessage;
    return res;
  }
  const loadOrCreateCart = async (id) => {
    loading.value = true;
    let cartID;
    if (typeof id !== 'undefined' && id !== null) {
      cartID = id;
    } else {
      cartID = checkIfCartExist();
    }
    const res = await hubbleClient.cart.loadOrCreateCart(cartID)
    loading.value = false;
    actionResponse.value = res.data;
    cart.value = actionResponse.value.responseObject;
    cartStore.setCart(cart.value)
    appContext.$storage.setLocalStorage('tlmCartID', cart.value.id);
    notificationAvail.value = true;
    notificationMessage.value = actionResponse.value.actionMessage;
    getScenario()
    return res;
  }

  const getCategoryForCartItemGroup = () => {
    const id = getAttrFromCartScenario(cart.value, 'categoryId')
    return id
  }

  const isCartEmpty = computed(() => loading.value ? false : cart.value.cartEmpty)
  const skuListForStockCheck = computed(() => {
    const skuList = []
    cart.value.cartItemGroupList.forEach(group => {
      group.cartItems.forEach(cartItem => {
        if (cartItem.cartProduct.productTypeKey !== PRODUCT_TYPE.MOBILE_SUBSCRIPTION) {
          skuList.push(cartItem.sku)
        }
      })
    })
    return skuList
  })

  const checkCartItems = (updatedCart) => {
    const currentCartItems = cart.value?.cartItemGroupList
    let output = true
    // check if the items in cart has changed NLOS-2472
    try {
      const newCart = updatedCart
      currentCartItems.forEach((item, i) => {
        const newItem = newCart.cartItemGroupList[i]
        item.cartItems.forEach((cartItem, j) => {
          const newCartItem = newItem.cartItems[j]
          if (newCartItem.sku !== cartItem.sku) {
            $nuxt.$emit('cartItemsChanged')
            output = false
          }
        })
      })
    } catch (error) {
      console.error(error);
    }
    return output
  }


  const updateCart = (updatedCart) => {
    if (updatedCart) {
      checkCartItems(updatedCart)
      cart.value = updatedCart;
      cartStore.setCart(cart.value)
    }
  }

  const deviceList = computed(() => {
    const deviceItems = []
    cart?.value?.cartItemGroupList?.forEach(cartItemGroup => {
      const device = cartItemGroup.cartItems.find(cartItem => !isSubscription(cartItem.cartProduct.productTypeKey))
      if (device !== undefined) {
        return deviceItems.push(device)
      }
    })
    // when BE can handle multiple devices, we will send it as an array of values
    return deviceItems[0]
  })
  const subscriptionList = computed(() => {
    const carteItemsWiThSubscription = []
    cart?.value?.cartItemGroupList?.forEach(cartItemGroup => {
      const subscription = cartItemGroup.cartItems.find(cartItem => isSubscription(cartItem.cartProduct.productTypeKey))
      if (subscription !== undefined) {
        return carteItemsWiThSubscription.push(subscription)
      }
    })
    // when BE can handle multiple devices, we will send it as an array of values
    return carteItemsWiThSubscription[0]
  })
  const installments = computed(() => {
    const installments = []
    cart?.value?.cartItemGroupList?.forEach(cartItemGroup => {
      const device = cartItemGroup.cartItems.find(cartItem => !isSubscription(cartItem.cartProduct.productTypeKey))
      if (device !== undefined) {
        return installments.push(device.installmentPeriod)
      }
    })
    // when BE can handle multiple installments, we will send it as an array of values
    return installments.length > 0 ? installments[0] : 0
  })

  const isSubscriptionOnly = computed(() => { // (sim-only)
    let subscriptionOnly = false
    cart?.value?.cartItemGroupList?.forEach(cartItemGroup => {
      const notSubscription = cartItemGroup.cartItems.find(cartItem => !isSubscription(cartItem.cartProduct.productTypeKey))
      if (!notSubscription) {
        subscriptionOnly = true
      }
    })
    return subscriptionOnly
  })
  const isMobileVoiceSubscriptionOnly = computed(() => { // ( Mobile voice sim-only)
    let mvSubscriptionOnly = false
    cart?.value?.cartItemGroupList?.forEach(cartItemGroup => {
      const notASubscription = cartItemGroup.cartItems.find(cartItem => !isMobileVoiceSubscription(cartItem.cartProduct.productTypeKey))
      if (!notASubscription) {
        mvSubscriptionOnly = true
      }
    })
    return mvSubscriptionOnly
  })


  const hasInstallment = (cartItem) => {
    return cartItem.installmentPeriod > 0
  }
  const getItemAttributeFromCartItem = (cartItem, itemAttribute) => {
    return cartItem?.itemAttributes?.find(attr => attr.name === itemAttribute) !== undefined
      ? cartItem?.itemAttributes?.find(attr => attr.name === itemAttribute).value
      : null
  }
  const mixPoList = computed(() => {
    const mixPoList = getItemAttributeFromCartItem(subscriptionList.value, 'PlayPoList')
    return mixPoList !== null ? mixPoList.split(',').map(val => val) : null
  })
  const isNumberPorting = (cartItem) => {
    return getItemAttributeFromCartItem(cartItem, 'IsNumberPorting') === 'true'
  }
  const hasPhoneNumber = (cartItem) => {
    return getItemAttributeFromCartItem(cartItem, 'PhoneNumber') !== null
  }
  const getItemAttributesForSubscriptionFromCart = (attrName) => {
    let attrVal = null
    cart?.value?.cartItemGroupList?.forEach(cartItemGroup => {
      const subscriptionInCart = cartItemGroup.cartItems.find(cartItem => isSubscription(cartItem.cartProduct.productTypeKey))
      attrVal = subscriptionInCart !== undefined ? getItemAttributeFromCartItem(subscriptionInCart, attrName) : null
    })
    return attrVal
  }
  const isNewNumber = (cartItem) => {
    return !isNumberPorting(cartItem) && hasPhoneNumber(cartItem)
  }
  const isNewNumberSelected = (cartItem, subscription) => {
    return isNewNumber(cartItem) || subscription?.menu?.value === CUSTOMER_SUBSCRIPTION_TYPE.NEW_NUMBER
  }
  const isNumberPortingSelected = (cartItem, subscription) => {
    return isNumberPorting(cartItem) || subscription?.menu?.value === CUSTOMER_SUBSCRIPTION_TYPE.MOVE_NUMBER
  }
  const isChangeSubscription = (subscription) => {
    return subscription?.menu?.value === CUSTOMER_SUBSCRIPTION_TYPE.CHANGE_SUBSCRIPTION
  }
  const isKeepSubscription = (subscription) => {
    return subscription?.menu?.value === CUSTOMER_SUBSCRIPTION_TYPE.KEEP_SUBSCRIPTION
  }
  const isAnyItemInPreOrderOrBackOrder = computed(() => {
    let isAnyItemInPreOrderOrBackOrder = false
    cart?.value?.cartItemGroupList?.forEach(cartItemGroup => {
      const carteItemWiThPurchaseCategory = cartItemGroup.cartItems.find(cartItem => {
        const purchaseCategory = getItemAttributeFromCartItem(cartItem, 'PurchaseCategory')
        return PURCHASE_CATEGORY.PRE_ORDER === purchaseCategory || PURCHASE_CATEGORY.BACK_ORDER === purchaseCategory
      })
      if (carteItemWiThPurchaseCategory !== undefined) {
        isAnyItemInPreOrderOrBackOrder = true;
      }
    })
    return isAnyItemInPreOrderOrBackOrder;
  })

  const isEsimProduct = product => {
    return (product.productAttributes.find(attr => {
      return attr.name === 'E-SIM' ? (attr.value === 'true') : false
    })?.value === 'true')
  }

  const hasAnyEsim = () => {
    let anyEsim = false
    cart?.value?.cartItemGroupList?.forEach(cartItemGroup =>
      cartItemGroup.cartItems.find(cartItem => {
        if (isEsimProduct(cartItem.cartProduct)) {
          anyEsim = true
          return true
        }
        return false
      }
      ));

    return anyEsim
  }

  const paymentOptions = computed(() => {
    return cart.value.paymentOptions
  })
  const isPayGSubscription = (bundleType) => {
    return bundleType === BUNDLE_TYPE.PAYG
  }
  const billingAddressFromCart = computed(() => {
    return cart.value.billingAddress
  })
  const containsKeepSubscription = computed(() => cart?.value && cart?.value?.keepSubscriptionType)
  const customerOutOfDiscountedPeriod = computed(() => cart?.value && cart?.value?.customerOutOfDiscountedPeriod)
  return {
    isSubscriptionOnly,
    hasPhoneNumber,
    billingAddressFromCart,
    getItemAttributesForSubscriptionFromCart,
    isMobileVoiceSubscriptionOnly,
    isPayGSubscription,
    getScenario,
    paymentOptions,
    isMobileBroadBandSubscription,
    hasAnyEsim,
    getCategoryForCartItemGroup,
    isAnyItemInPreOrderOrBackOrder,
    checkCartItems,
    cprNumber,
    cvrNumber,
    isProtectedAddress,
    isNewNumber,
    isChangeSubscription,
    isNumberPortingSelected,
    isNewNumberSelected,
    isNumberPorting,
    getItemAttributeFromCartItem,
    cart: computed(() => cart.value),
    getCartID: computed(() => cart.value.id),
    cartID: computed(() => cart.value.id),
    isLoading: computed(() => loading.value),
    notificationAvailable: computed(() => notificationAvail.value),
    notificationMessage: computed(() => notificationMessage.value),
    actionResponse: computed(() => actionResponse.value),
    cartLoaded: computed(() => cart?.value && Object.keys(cart.value).length > 0),
    containsKeepSubscription,
    customerOutOfDiscountedPeriod,
    updateCart,
    notificationRead: () => { notificationAvail.value = false },
    addCartItem,
    createCart,
    deleteCart,
    deleteCartCookie,
    deleteCartGroupItem,
    emptyCart,
    createCartOrder,
    loadOrCreateCart,
    isCartEmpty,
    skuListForStockCheck,
    subscriptionList,
    mixPoList,
    installments,
    isSubscription,
    hasInstallment,
    deviceList,
    setPaymentRef: (payRef) => { paymentRef.value = payRef; },
    getCartById,
    checkIfCartExist,
    isKeepSubscription,
    keepSubCustInDiscountedPeriod: computed(() => containsKeepSubscription.value ? !customerOutOfDiscountedPeriod.value : true)
  }
}
